/**
 * PlayersSection - Simple player list for Americano (no positions, no fixed pairs)
 */
import React, { useState, useEffect, useRef } from 'react';
import { useTournament } from '../../contexts/TournamentContext';
import { useToast } from '../../contexts/ToastContext';

const PlayersSection = () => {
  const { state, setPlayers, updatePlayerNames } = useTournament();
  const { config, players: savedPlayers, scheduleGenerated } = state;
  const { showError } = useToast();

  const [names, setNames] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const previousNames = useRef([]);

  useEffect(() => {
    if (!config || initialized) return;

    const count = config.playerCount || 8;

    if (savedPlayers && savedPlayers.length === count) {
      setNames(savedPlayers.map(p => p.name));
    } else {
      setNames(Array.from({ length: count }, (_, i) => `Joueur ${i + 1}`));
    }
    setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, initialized]);

  // When playerCount changes, resize the list
  useEffect(() => {
    if (!initialized) return;
    const count = config?.playerCount || 8;
    setNames(prev => {
      if (prev.length === count) return prev;
      if (prev.length < count) {
        return [...prev, ...Array.from({ length: count - prev.length }, (_, i) => `Joueur ${prev.length + i + 1}`)];
      }
      return prev.slice(0, count);
    });
  }, [config?.playerCount, initialized]);

  // Sync names → context
  useEffect(() => {
    if (!initialized || names.length === 0) return;
    if (scheduleGenerated) {
      updatePlayerNames(names);
    } else {
      setPlayers(names.map(name => ({ name })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [names, initialized]);

  const handleChange = (index, value) => {
    setNames(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleFocus = (e, index) => {
    previousNames.current[index] = names[index];
    e.target.select();
  };

  const handleBlur = (index) => {
    const current = names[index].trim();
    if (!current) {
      setNames(prev => {
        const updated = [...prev];
        updated[index] = previousNames.current[index];
        return updated;
      });
      return;
    }
    const isDuplicate = names.some(
      (n, i) => i !== index && n.trim().toLowerCase() === current.toLowerCase()
    );
    if (isDuplicate) {
      showError(`Le nom "${current}" est déjà utilisé`);
      setNames(prev => {
        const updated = [...prev];
        updated[index] = previousNames.current[index];
        return updated;
      });
    }
  };

  if (!config) return null;

  return (
    <div className="section-inner">
      <div className="section-header">
        <h1><i className="bi bi-people-fill me-3"></i>Joueurs</h1>
      </div>

      <div className="section-body">
        <div className="players-list">
          {names.map((name, index) => (
            <div key={index} className="player-pair-row">
              <div className="pair-header" style={{ cursor: 'default' }}>
                <div className="pair-avatar">
                  <span className="pair-number">#{index + 1}</span>
                </div>
                <div className="flex-grow-1 px-2">
                  <input
                    type="text"
                    className="form-control"
                    value={name}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onFocus={(e) => handleFocus(e, index)}
                    onBlur={() => handleBlur(index)}
                    maxLength={30}
                    autoComplete="off"
                    placeholder={`Joueur ${index + 1}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlayersSection;
